import React, { useEffect, useRef, useState } from "react";
import axios from "axios";

/* ======================================
   FALLBACK STORIES DATA
====================================== */

const fallbackStories = [
  {
    id: 1,
    category: "Sports",
    slides: [
      {
        image: "https://picsum.photos/id/1011/500/800",
        heading: "India won the match by 7 wickets",
      },
      {
        image: "https://picsum.photos/id/1012/500/800",
        heading: "Virat scored brilliant century",
      },
      {
        image: "https://picsum.photos/id/1013/500/800",
        heading: "Celebrations across the country",
      },
    ],
  },
  {
    id: 2,
    category: "Technology",
    slides: [
      {
        image: "https://picsum.photos/id/1015/500/800",
        heading: "Tech stocks are rising fast",
      },
      {
        image: "https://picsum.photos/id/1016/500/800",
        heading: "AI market growth expected",
      },
    ],
  },
  {
    id: 3,
    category: "World",
    slides: [
      {
        image: "https://picsum.photos/id/1020/500/800",
        heading: "Global political updates",
      },
      {
        image: "https://picsum.photos/id/1024/500/800",
        heading: "International summit meeting",
      },
    ],
  },
];

const WebStoriesPage = () => {
  const sliderRef = useRef(null);
  const homeIntervalRef = useRef(null);
  const storyIntervalRef = useRef(null);

  const [stories, setStories] = useState([]);
  const [storiesLoaded, setStoriesLoaded] = useState(false);
  const [activeStoryIndex, setActiveStoryIndex] = useState(null);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  const categoryLabel = (value) => {
    if (!value) return "वेब स्टोरी";
    const key = value.toLowerCase().trim();
    const map = {
      sports: "खेल",
      sport: "खेल",
      technology: "टेक",
      tech: "टेक",
      world: "दुनिया",
      politics: "राजनीति",
      cinema: "सिनेमा",
      entertainment: "मनोरंजन",
      business: "व्यापार",
      health: "स्वास्थ्य",
      lifestyle: "लाइफस्टाइल",
      education: "शिक्षा",
      crime: "अपराध",
      viral: "वायरल",
      local: "स्थानीय",
      national: "देश",
      international: "विदेश",
      "web story": "वेब स्टोरी",
    };
    return map[key] || value;
  };

  const activeStory =
    activeStoryIndex !== null ? stories[activeStoryIndex] : null;

  const loopedStories = stories.length ? [...stories, ...stories] : [];

  const scrollLeft = () => {
    if (sliderRef.current) {
      sliderRef.current.scrollBy({
        left: -300,
        behavior: "smooth",
      });
    }
  };

  const scrollRight = () => {
    if (sliderRef.current) {
      sliderRef.current.scrollBy({
        left: 300,
        behavior: "smooth",
      });
    }
  };

  /* ===============================
     FETCH STORIES
  =============================== */

  useEffect(() => {
    const fetchStories = async () => {
      try {
        const baseApi = process.env.REACT_APP_BACKEND_URL;
        const urls = [`${baseApi}story`, `${baseApi}stories`];
        let data = null;

        for (const url of urls) {
          try {
            const res = await axios.get(url);
            if (Array.isArray(res.data)) {
              data = res.data;
              break;
            }
          } catch (err) {
            // try next url
          }
        }

        if (data) {
          const baseUrl = baseApi.replace("/api/v1/", "");
          const resolveImage = (img) => {
            if (!img) return "/no-image.png";
            if (img.startsWith("http") || img.startsWith("/")) return img;
            return `${baseUrl}/uploads/stories/${img}`;
          };

          const mapped = data
            .map((item) => {
              const extraImages = Array.isArray(item.images) ? item.images : [];
              const extraHeadings = Array.isArray(item.image_headings)
                ? item.image_headings
                : [];

              const extraSlides = extraImages.map((img, idx) => ({
                image: resolveImage(img),
                heading: extraHeadings[idx] || item.heading,
              }));

              return {
                id: item.id,
                category: item.category || "Web Story",
                slides: [
                  {
                    image: resolveImage(item.image),
                    heading: item.heading,
                  },
                  ...extraSlides,
                ].filter((s) => s.heading),
              };
            })
            .filter((item) => item.slides.length > 0);

          setStories(mapped);
        } else {
          setStories([]);
        }
      } catch (err) {
        console.error("Failed to fetch stories", err);
        setStories(fallbackStories);
      } finally {
        setStoriesLoaded(true);
      }
    };

    fetchStories();
  }, []);

  useEffect(() => {
    if (activeStoryIndex !== null && activeStoryIndex >= stories.length) {
      closeStory();
    }
  }, [stories.length]);

  /* ===============================
     HOME AUTO SLIDER
  =============================== */

  useEffect(() => {
    const slider = sliderRef.current;
    if (!slider || stories.length === 0) return;

    homeIntervalRef.current = setInterval(() => {
      slider.scrollBy({ left: 260, behavior: "smooth" });

      const halfWidth = slider.scrollWidth / 2;
      if (slider.scrollLeft >= halfWidth) {
        slider.scrollLeft = slider.scrollLeft - halfWidth;
      }
    }, 2500);

    return () => clearInterval(homeIntervalRef.current);
  }, [stories.length]);

  /* ===============================
     STORY AUTO SLIDE
  =============================== */

  useEffect(() => {
    if (!activeStory || isPaused) return;

    storyIntervalRef.current = setInterval(() => {
      setCurrentSlide((prev) => {
        if (prev < activeStory.slides.length - 1) {
          return prev + 1;
        } else {
          if (activeStoryIndex < stories.length - 1) {
            setActiveStoryIndex((prevIndex) => prevIndex + 1);
            return 0;
          } else {
            closeStory();
            return prev;
          }
        }
      });
    }, 3000);

    return () => clearInterval(storyIntervalRef.current);
  }, [activeStoryIndex, isPaused, stories.length]);

  const openStory = (index) => {
    setActiveStoryIndex(index);
    setCurrentSlide(0);
    setIsPaused(false);
  };

  const closeStory = () => {
    setActiveStoryIndex(null);
    setCurrentSlide(0);
    setIsPaused(false);
  };

  const nextSlide = () => {
    if (!activeStory) return;

    if (currentSlide < activeStory.slides.length - 1) {
      setCurrentSlide((prev) => prev + 1);
    } else {
      if (activeStoryIndex < stories.length - 1) {
        setActiveStoryIndex((prev) => prev + 1);
        setCurrentSlide(0);
      } else {
        closeStory();
      }
    }
  };

  const prevSlide = () => {
    if (!activeStory) return;

    if (currentSlide > 0) {
      setCurrentSlide((prev) => prev - 1);
    } else if (activeStoryIndex > 0) {
      const previousStory = stories[activeStoryIndex - 1];
      setActiveStoryIndex((prev) => prev - 1);
      setCurrentSlide(previousStory.slides.length - 1);
    }
  };

  const handleShare = async () => {
    if (!activeStory) return;

    if (navigator.share) {
      await navigator.share({
        title: activeStory.slides[currentSlide].heading,
        url: window.location.href,
      });
    } else {
      alert("Sharing not supported in this browser");
    }
  };

  return (
    <>
      {/* ================= HOME ================= */}
      <div className=" bg-gray-50 pt-6">
        <section className="bg-pink-100 p-2 sm:p-5 rounded-lg relative">
          {stories.length === 0 && storiesLoaded ? (
            <div className="text-sm text-gray-600 px-3 py-8">
              No stories available.
            </div>
          ) : (
            <div
              ref={sliderRef}
              className="flex gap-4 overflow-x-auto scroll-smooth hide-scrollbar"
            >
              {loopedStories.map((story, index) => {
                const latest = story.slides[story.slides.length - 1];

                return (
                  <div
                    key={`${story.id}-${index}`}
                    onClick={() => openStory(index % stories.length)}
                    className="min-w-[70%] sm:min-w-[240px] h-[320px] sm:h-[400px] rounded-xl overflow-hidden relative cursor-pointer transition duration-300"
                  >
                    <img
                      src={latest.image}
                      alt={latest.heading}
                      className="w-full h-full object-cover"
                    />

                    <span className="absolute top-3 left-3 bg-red-600 text-white text-xs px-3 py-1 rounded-full">
                      {categoryLabel(story.category)}
                    </span>

                    <div className="absolute inset-x-0 bottom-0 p-4 bg-gradient-to-t from-black/85 via-black/40 to-transparent">
                      <p className="text-white font-semibold text-[16px] leading-snug text-center drop-shadow line-clamp-3">
                        {latest.heading}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* auto-slider only (no manual arrows) */}
        </section>

        <div className="flex justify-center mt-4">
          <div className="flex items-center gap-3 sm:gap-4">
            <button
              onClick={scrollLeft}
              className="w-9 h-9 sm:w-10 sm:h-10 flex items-center justify-center bg-white shadow-md rounded-full text-gray-700 hover:bg-gray-200 transition"
              aria-label="Scroll left"
            >
              <i className="fa-solid fa-chevron-left"></i>
            </button>
            <button
              onClick={scrollRight}
              className="w-9 h-9 sm:w-10 sm:h-10 flex items-center justify-center bg-white shadow-md rounded-full text-gray-700 hover:bg-gray-200 transition"
              aria-label="Scroll right"
            >
              <i className="fa-solid fa-chevron-right"></i>
            </button>
          </div>
        </div>
      </div>

      {/* ================= STORY VIEWER ================= */}
      {activeStory && (
        <div className="fixed inset-0 bg-black z-[999] flex items-center justify-center">
          <div className="relative w-full h-[100dvh] sm:h-[95vh] sm:max-w-md sm:rounded-xl overflow-hidden bg-black">
            {/* PROGRESS BARS */}
            <div className="absolute top-4 left-3 right-3 flex gap-2 z-20">
              {activeStory.slides.map((_, index) => (
                <div
                  key={index}
                  className="flex-1 h-1 bg-gray-600/70 rounded overflow-hidden"
                >
                  <div
                    className={`h-full bg-white ${
                      index === currentSlide && !isPaused
                        ? "animate-progress"
                        : index < currentSlide
                        ? "w-full"
                        : "w-0"
                    }`}
                    style={{
                      animationDuration: "3000ms",
                      animationFillMode: "forwards",
                    }}
                  ></div>
                </div>
              ))}
            </div>

            {/* HEADER */}
            <div className="absolute top-10 left-4 right-4 flex justify-between items-center text-white z-20">
              <div className="flex items-center gap-4">
                <button
                  onClick={closeStory}
                  className="text-xl sm:text-2xl"
                >
                  <i className="fa-solid fa-xmark"></i>
                </button>

                <button
                  onClick={() => setIsPaused(!isPaused)}
                  className="text-lg sm:text-xl"
                >
                  {isPaused ? (
                    <i className="fa-solid fa-play"></i>
                  ) : (
                    <i className="fa-solid fa-pause"></i>
                  )}
                </button>
              </div>

              <button
                onClick={handleShare}
                className="text-lg sm:text-xl"
              >
                <i className="fa-solid fa-share-nodes"></i>
              </button>
            </div>

            {/* IMAGE */}
            <img
              key={currentSlide}
              src={activeStory.slides[currentSlide].image}
              alt=""
              className="w-full h-full object-cover"
            />

            {/* HEADING */}
            <div className="absolute bottom-6 sm:bottom-8 left-4 right-4 text-white bg-black/50 backdrop-blur-md p-4 rounded-lg">
              <h2
                key={currentSlide}
                className="text-base sm:text-lg md:text-xl text-center font-semibold animate-slideFadeUp"
              >
                {activeStory.slides[currentSlide].heading}
              </h2>
            </div>

            {/* LEFT */}
            <button
              onClick={prevSlide}
              className="absolute left-2 sm:left-4 top-1/2 -translate-y-1/2 text-white text-2xl sm:text-3xl z-20"
            >
              <i className="fa-solid fa-chevron-left"></i>
            </button>

            {/* RIGHT */}
            <button
              onClick={nextSlide}
              className="absolute right-2 sm:right-4 top-1/2 -translate-y-1/2 text-white text-2xl sm:text-3xl z-20"
            >
              <i className="fa-solid fa-chevron-right"></i>
            </button>
          </div>
        </div>
      )}
    </>
  );
};

export default WebStoriesPage;
